import React, { JSX } from "react";
import UploadIcon from "@mui/icons-material/Upload";
import { Box, Button, Typography } from "@mui/material";
import { useAppDispatch } from "../../../store/hooks";
import CustomHeader from "../../Lab/LabPrint/CustomHeader";
import ImageCrop from "../../../components/ImageCrop/ImageCrop";
import { notificationAdd } from "../../../actions/notification";

const BillPrintCustomHeader = ({
  title = "Custom Print Header (894px * 112px)",
  description = "This image will appear at the top of print templates",
  onSave,
  currentImage,
  onRemove,
  ratio = { x: 8, y: 1 }
}: {
  title?: string;
  description?: string;
  currentImage: string;
  onSave: (img: Blob) => void;
  onRemove: () => void;
  ratio?: { x: number; y: number };
}): JSX.Element => {
  const fileUploaderRef = React.useRef<HTMLInputElement>(null);
  const [tempImg, setTempImg] = React.useState<string | null>(null);
  const [isCropperOpen, setIsCropperOpen] = React.useState(false);
  const dispatch = useAppDispatch();

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e?.target?.files?.length) {
      return;
    }
    if (e?.target?.files[0]?.size >= 200000) {
      // show error dialog if file size exceeds 200KB
      // eslint-disable-next-line no-param-reassign
      e.target.value = "";
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: "File size needs to be less than 200KB.",
          autoTimeout: true
        })
      );
      return;
    }
    const file = e?.target?.files[0];
    const extensionIndex = file?.name?.lastIndexOf(".") + 1; // getting starting index of file extension
    // getting extension name
    const extension = file?.name?.substr(extensionIndex)?.toLowerCase();
    if (extension === "jpg" || extension === "jpeg" || extension === "png" || extension === "gif") {
      setTempImg(URL.createObjectURL(file));
      setIsCropperOpen(true);
    } else {
      // eslint-disable-next-line no-param-reassign
      e.target.value = "";
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: "Invalid image type.",
          autoTimeout: true
        })
      );
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: "flex", flexDirection: "column" }}>
        <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {description}
        </Typography>
      </Box>
      {currentImage && <CustomHeader imageUrl={currentImage} />}

      {!currentImage && (
        <div>
          <input
            className="imageUpload"
            data-testmation="uploadBannerInput"
            type="file"
            accept=".jpg,.jpeg,.gif,.png"
            ref={fileUploaderRef}
            name="fileUploader"
            onChange={handleImageChange}
            id="fileUploader"
            style={{ display: "none" }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "112px",
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 1,
              backgroundColor: "action.hover"
            }}
          >
            <Button
              variant="outlined"
              onClick={(e) => {
                e.preventDefault();
                fileUploaderRef.current?.click();
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}
            >
              <UploadIcon sx={{ height: 16, width: 16 }} />
              Upload Custom Header
            </Button>
          </Box>
        </div>
      )}

      {currentImage && (
        <Button variant="contained" sx={{ backgroundColor: "red" }} onClick={onRemove}>
          Remove Header Image
        </Button>
      )}

      {/* crop image */}
      <ImageCrop
        image={tempImg}
        dynamicRatio={false}
        defaultRatio={ratio}
        isOpen={isCropperOpen}
        onClose={() => setIsCropperOpen(false)}
        onSave={onSave}
      />
    </Box>
  );
};

export default BillPrintCustomHeader;
